/**
 * P2-S3 룸 모드 배치 — 모드 정의 room 블록 + 인원(팀) → 환경 배치(프리셋 env 를 채운 파생 정의) · 경기장 · 레인 · 자리.
 * 룸 서버가 모드를 바꿀 때 부른다(순수 함수 · 같은 입력이면 같은 배치 — 결정론).
 *
 *  - 스모(SR-MODE-04): 링 ≤ arenas(5열) · 링마다 두 자리(마주 보기) · 대기석(뒤쪽 줄) — 링 중심 거리 > R 이면 패
 *  - 협동(SR-MODE-05): 팀마다 레인(폭 3 m) — 팀 크기에 맞는 물체(cargo) · 목표 존(앞 4 m) · 팀원 자리(물체 뒤) · 구경석
 *  - 릴레이(SR-MODE-07 · FIELD-DEC-003 §3.3): 구간 틀을 columns 열 뱀 순서로(되돌아오는 줄 yaw 180) — {@link relayLayout}
 *  - P3-S4 교류 룸(SR-ROOM-05 · 기계 ≤ 60): 기계 30 이하 배치는 한 바이트도 바꾸지 않는다(기존 권위 리플레이 환경 해시 불변).
 *    넘으면 스모 링 6열(≤30) · 협동 레인 두 줄(≤16 · 뒤 줄 +{@link CARRY_BANK_PITCH} m) · 대기석 네 줄 — 상한은 {@link arenaCap}.
 *    릴레이는 반 룸만(Q-3) — 그대로 30.
 */
import type { RelayStagePlan } from "./relay";
import type {
  EnvPlacement,
  ModeDefinitionV1,
  RelayTrigger,
  RoomModeDef,
  Slot,
  Vec3,
  Yaw,
} from "./types";

export interface ArenaInfo {
  readonly index: number;
  /** 링 중심(x, z · m) */
  readonly center: readonly [number, number];
  readonly radius: number;
  /** 링 윗면 높이(m) */
  readonly top: number;
  readonly slots: readonly [Slot, Slot];
}

export interface LaneInfo {
  readonly index: number;
  readonly teamSize: number;
  readonly cargo: { readonly key: string; readonly pieceId: string; readonly titleKey?: string };
  /** 물체 처음 자리(밑면 중심) */
  readonly start: Vec3;
  readonly goal: { readonly id: string; readonly center: Vec3; readonly size: Vec3 };
  readonly slots: readonly Slot[];
}

export interface RoomLayout {
  /** 프리셋 env 를 채운 파생 정의(createPlayground 에 그대로) */
  readonly def: ModeDefinitionV1;
  readonly presetKey: string;
  readonly kind: RoomModeDef["kind"];
  readonly arenas: readonly ArenaInfo[];
  readonly lanes: readonly LaneInfo[];
  /** 대기·구경 자리 */
  readonly bench: readonly Slot[];
}

const r3 = (v: number) => Math.round(v * 1000) / 1000;

function derive(def: ModeDefinitionV1, presetKey: string, env: EnvPlacement[]): ModeDefinitionV1 {
  const p = def.presets.find((x) => x.key === presetKey) ?? def.presets[0]!;
  return { ...def, presets: [{ ...p, env }] };
}

/** 반 룸 정원(SR-ROOM-01) — 이 이하 배치는 P3-S4 전과 같다 */
export const CLASS_MAX_MACHINES = 30;
/** 교류 룸 정원(SR-ROOM-05 · 두 반) */
export const EXCHANGE_MAX_MACHINES = 60;

/** 대기석 자리 수 — 반 룸 30(두 줄) · 교류 룸 60(네 줄 · 1.2 m 간격) */
export const benchSize = (machines: number): number =>
  machines > CLASS_MAX_MACHINES ? EXCHANGE_MAX_MACHINES : CLASS_MAX_MACHINES;

/**
 * 링·레인 상한 — 기계가 반 룸 정원을 넘으면(교류 룸) room.exchangeArenas, 없으면 arenas 의 두 배(정원이 두 배 · 최대 30).
 * 게시된 v1 정의(pushout-ring 15 · carry-together 8)는 DB 에서 바꿀 수 없어(ModeService — 게시 버전 불변) 규칙으로 둔다.
 */
export function arenaCap(room: RoomModeDef, machines: number, fallback: number): number {
  const base = room.arenas ?? fallback;
  if (machines <= CLASS_MAX_MACHINES) return base;
  return Math.max(base, room.exchangeArenas ?? Math.min(30, base * 2));
}

/** 대기석 — 15자리 한 줄(1 m) · 줄은 뒤로 1.2 m(z 감소) */
function benchRow(n: number, z: number): Slot[] {
  const out: Slot[] = [];
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / 15);
    out.push({ at: [r3(-7 + (i % 15)), 0, r3(z - row * 1.2)], yaw: 0 });
  }
  return out;
}

/**
 * 스모 — 링 수 = min(arenas, ⌊기계 수 / 2⌋) (≥1) · 5열(링 ≤15 — 3줄) · 교류 룸(링 16~30)은 6열 ≤5줄.
 * 링이 3줄을 넘으면 바닥을 앞(+z)으로 늘린다(대기석 · 첫 줄 자리는 그대로).
 */
export function sumoLayout(
  def: ModeDefinitionV1,
  machines: number,
  presetKey = "standard",
): RoomLayout {
  const room = def.room;
  if (room?.kind !== "sumo" || !room.ring) throw new RangeError(`스모 모드가 아니다: ${def.id}`);
  const R = room.ring.radius;
  const h = room.ring.height ?? 0.1;
  const n = Math.max(1, Math.min(arenaCap(room, machines, 15), Math.floor(machines / 2)));
  const cols = n <= 15 ? Math.min(5, n) : 6;
  const gap = 2 * R + 1.6;
  const extra = Math.max(0, Math.ceil(n / cols) - 3) * gap;
  const env: EnvPlacement[] = [
    {
      prefab: "platform",
      id: "floor",
      at: [0, 0, extra ? r3(3 + extra / 2) : 3],
      size: [r3(cols * gap + 16), 0.2, extra ? r3(26 + extra) : 26],
    },
  ];
  const arenas: ArenaInfo[] = [];
  for (let i = 0; i < n; i++) {
    const cx = r3((i % cols) * gap - ((cols - 1) * gap) / 2);
    const cz = r3(Math.floor(i / cols) * gap + 1);
    env.push({ prefab: "ring", id: `ring-${i + 1}`, at: [cx, h, cz], size: [2 * R, h, 2 * R] });
    const d = r3(R / 2);
    arenas.push({
      index: i,
      center: [cx, cz],
      radius: R,
      top: h,
      slots: [
        { at: [cx, h, r3(cz - d)], yaw: 0 },
        { at: [cx, h, r3(cz + d)], yaw: 180 },
      ],
    });
  }
  return {
    def: derive(def, presetKey, env),
    presetKey,
    kind: "sumo",
    arenas,
    lanes: [],
    bench: benchRow(benchSize(machines), -5),
  };
}

/** 팀 크기 목록(합 ≤ 기계 수) — 룸 멤버를 min..max 로 고르게 나눈다(3명 우선 · 남으면 2·4로) */
export function teamSizes(members: number, min = 2, max = 4, maxTeams = 8): number[] {
  if (members < min) return [];
  let teams = Math.min(maxTeams, Math.max(1, Math.round(members / 3)));
  while (teams > 1 && members / teams < min) teams--;
  while (teams < maxTeams && members / teams > max) teams++;
  const out = new Array(teams).fill(Math.floor(members / teams)) as number[];
  let rest = members - out.reduce((a, b) => a + b, 0);
  for (let i = 0; rest > 0 && i < teams; i++, rest--) out[i]!++;
  return out.map((x) => Math.min(max, Math.max(min, x)));
}

/** 협동 레인 한 줄 상한 — 넘으면(교류 룸) 두 줄로 */
export const CARRY_BANK_LANES = 8;
/** 협동 뒤 줄 레인의 z 간격(m) — 앞 줄 목표 존(z 5)과 뒤 줄 팀원(z ≈ 9.4) 사이 ≥ 3.5 m */
export const CARRY_BANK_PITCH = 9;

/**
 * 협동 — 팀마다 레인 · 물체는 팀 크기에 맞는 cargo(없으면 가장 가까운 것).
 * 레인 ≤ 8 은 한 줄(P2-S3 그대로) · 넘으면 두 줄(⌈n/2⌉ 씩 · 뒤 줄은 +{@link CARRY_BANK_PITCH} m) — 바닥 폭이 8레인 때를 넘지 않는다.
 * machines: 룸 명부 수(구경석 포함) — 팀 인원 합과 큰 쪽이 반 룸 정원을 넘으면 대기석 60 · 레인 상한 {@link arenaCap}
 */
export function carryLayout(
  def: ModeDefinitionV1,
  teams: readonly number[],
  presetKey = "standard",
  machines = 0,
): RoomLayout {
  const room = def.room;
  if (room?.kind !== "carry" || !room.cargo?.length)
    throw new RangeError(`협동 모드가 아니다: ${def.id}`);
  const members = Math.max(
    machines,
    teams.reduce((a, b) => a + b, 0),
  );
  const n = Math.max(1, Math.min(arenaCap(room, members, 8), teams.length));
  const W = 3;
  const banks = n > CARRY_BANK_LANES ? 2 : 1;
  const per = Math.ceil(n / banks);
  const extra = (banks - 1) * CARRY_BANK_PITCH;
  const env: EnvPlacement[] = [
    {
      prefab: "platform",
      id: "floor",
      at: [0, 0, extra ? r3(2 + extra / 2) : 2],
      size: [r3(per * W + 16), 0.2, extra ? r3(20 + extra) : 20],
    },
  ];
  const lanes: LaneInfo[] = [];
  for (let i = 0; i < n; i++) {
    const size = teams[i] ?? 2;
    const cargo =
      room.cargo.find((c) => c.teamSize === size) ??
      [...room.cargo].sort((a, b) => Math.abs(a.teamSize - size) - Math.abs(b.teamSize - size))[0]!;
    const k = i % per;
    const oz = Math.floor(i / per) * CARRY_BANK_PITCH;
    const cx = r3(k * W - ((per - 1) * W) / 2);
    const pieceId = `cargo-${i + 1}`;
    const start: Vec3 = [cx, 0, 1 + oz];
    env.push({
      prefab: "crate",
      id: pieceId,
      at: start,
      size: cargo.size,
      mass: cargo.mass,
      ...(cargo.friction !== undefined ? { friction: cargo.friction } : {}),
    });
    const goal = {
      id: `goal-${i + 1}`,
      center: [cx, 0.3, 5 + oz] as Vec3,
      size: [1.2, 0.6, 1.2] as Vec3,
    };
    env.push({ prefab: "goal-zone", id: goal.id, at: [cx, 0, 5 + oz], size: goal.size });
    // 팀원 — 물체 뒤 한 줄(앞을 본다)
    const slots: Slot[] = [];
    for (let j = 0; j < size; j++)
      slots.push({
        at: [r3(cx + (j - (size - 1) / 2) * 0.35), 0, r3(1 + oz - cargo.size[2] / 2 - 0.35)],
        yaw: 0,
      });
    lanes.push({
      index: i,
      teamSize: size,
      cargo: { key: cargo.key, pieceId, ...(cargo.titleKey ? { titleKey: cargo.titleKey } : {}) },
      start,
      goal,
      slots,
    });
  }
  return {
    def: derive(def, presetKey, env),
    presetKey,
    kind: "carry",
    arenas: [],
    lanes,
    bench: benchRow(benchSize(members), -4),
  };
}

// ── P3-S1 릴레이(FIELD-DEC-003 §3.3) ─────────────────────────────────────

/** 구간 하나(월드 좌표 m) — 룸 서버 RelayCtl 이 관측 · 스냅에 쓴다 */
export interface RelaySection {
  /** 구간 번호(0 부터 · 화면은 +1) */
  readonly index: number;
  /** 배치의 기계 번호(RelayStagePlan.machine) */
  readonly machine: number;
  /** 구간 틀 원점(바닥 중심 · x, z) */
  readonly origin: readonly [number, number];
  /** 구간 틀 yaw — 뱀 순서 되돌아오는 줄은 180(흐름 −x) */
  readonly yaw: Yaw;
  /** 기계 자리(Armed 때 옮기기 · 재시도) */
  readonly machineSlot: Slot;
  /** 들어옴 자리(in = ball 이면 공 스냅 자리 · lever/zone 이면 신호 표시 자리) */
  readonly entry: Slot;
  /** 나감 판정 상자(중심 · 반 크기 — 월드 축 정렬) · 물체 중심이 이 안 = 나감(레버 감지 상자와 같다) */
  readonly exitBox: { readonly center: Vec3; readonly half: Vec3 };
  /** 나감 표시 존 조각 id(물리 없음) */
  readonly exitZone: string;
  /** 레버 조각 id(out = lever) — machineTouches(기계, lever) = 눌림 */
  readonly lever?: string;
  /** 구간 물체 조각 id(공 · 블록 — §3.2 규칙) · 동적 물체(mass > 0)라 Playground 물체 API 의 objectId 다 */
  readonly objectId?: string;
  /** 구간 물체 처음 자리 = Armed 스냅 자리(in = ball 이면 entry · 아니면 section.object) */
  readonly objectSlot?: Slot;
  readonly objectKind?: "ball" | "crate";
  readonly inKind: RelayTrigger;
  readonly outKind: RelayTrigger;
  /** 구간 타임아웃(초) — 배치 값 · 없으면 stageTimeoutS */
  readonly timeoutS: number;
}

export type RelayLayout = RoomLayout & {
  readonly kind: "relay";
  readonly sections: readonly RelaySection[];
  /** 공용 트랙(구간 자리 전체 — 배치 수와 무관하게 maxStages 기준 고정 격자) */
  readonly track: { readonly columns: number; readonly rows: number; readonly pitch: Vec3 };
};

/** 구간 물체 — 공(지름 0.16 m · 0.1 kg) · 블록(0.2 × 0.15 × 0.2 m · 0.1 kg · 마찰 0.5 — 대표 주행 모형이 밀 수 있게) */
export const RELAY_OBJECTS = {
  ball: { prefab: "ball", size: [0.16, 0.16, 0.16], mass: 0.1 },
  crate: { prefab: "crate", size: [0.2, 0.15, 0.2], mass: 0.1, friction: 0.5 },
} as const satisfies Record<
  "ball" | "crate",
  { prefab: "ball" | "crate"; size: Vec3; mass: number; friction?: number }
>;
/** 구간 벽 두께(m) — 구간 틀 안쪽 가장자리에 선다(틀 밖으로 나가지 않는다). 0.05 는 불도저가 공을 벽에 눌러 밀어 넣으면 뚫고 나갔다(P3-S1 실험) */
export const RELAY_WALL_T = 0.1;

/** 지역(구간 틀) → 월드: yaw 는 +z 를 +x 로 돌리는 쪽(YAW_QUAT 과 같다) · 정확한 표 */
const ROT: Record<Yaw, readonly [number, number]> = {
  0: [1, 0],
  90: [0, 1],
  180: [-1, 0],
  270: [0, -1],
};
const addYaw = (a: number, b: number) => ((((a + b) % 360) + 360) % 360) as Yaw;

/**
 * 릴레이 배치(순수 · 결정론) — 모드 정의 relay 블록 + 교사 배치(RelayStagePlan[]) → 파생 정의(env: 바닥 · 구간 벽 · 나감 존 ·
 * 레버 · 구간 물체) · 구간 정보 · 구경 자리. 리플레이 머리 plan.relay 로 같은 월드를 다시 만든다(FIELD-DEC-003 §8).
 *
 *  - 구간 자리는 **maxStages 기준 고정 격자**(columns 열 · 뱀 순서 · 되돌아오는 줄 yaw 180): 구간 i 의 월드 자리는 배치 수와
 *    무관하다 — 구간 하나만 있는 월드(내 구간 시험)와 30구간 월드에서 구간 0 이 같은 자리에 선다(§5 판정 일치).
 *  - 바닥: 24 × 18 m(격자가 더 크면 늘린다) · 격자는 뒤(−z)부터 · 구경 자리는 앞(+z) 가장자리 두 줄(트랙을 본다 — yaw 180).
 *  - 조각 id(구간 n = index + 1): s{n}-wall-in · -out · -l · -r · s{n}-exit(goal-zone · 물리 없음) · s{n}-lever(out = lever) ·
 *    s{n}-ball | s{n}-crate(구간 물체 · 동적). 환경 순서 = 구간 순서(floor 먼저) — 동적 물체 순서도 구간 순서다.
 *  - 구간 물체(§3.2): in = ball 이면 들어오는 공(entry 자리) · 아니고 out = ball 이면 공 · out = zone 이면 블록(section.object 자리) ·
 *    그 밖(out = lever)은 없음.
 * 검사(checkRelayChain)는 하지 않는다 — 부르는 쪽 몫(구간 하나 월드도 만들 수 있게). 구간 수 > maxStages 면 RangeError.
 */
export function relayLayout(
  def: ModeDefinitionV1,
  stages: readonly RelayStagePlan[],
  presetKey?: string,
): RelayLayout {
  const r = def.room?.kind === "relay" ? def.room.relay : undefined;
  if (!r) throw new RangeError(`릴레이 모드가 아니다: ${def.id}`);
  if (stages.length > r.maxStages)
    throw new RangeError(`구간 ${stages.length} > maxStages ${r.maxStages}`);
  const key = presetKey ?? def.presets[0]!.key;
  const sec = r.section;
  const [sx, wallH, sz] = sec.size;
  const gap = sec.gap ?? 0.6;
  const cols = Math.max(1, Math.min(sec.columns, r.maxStages));
  const rows = Math.ceil(r.maxStages / cols);
  const px = sx + gap;
  const pz = sz + gap;
  const gridW = cols * sx + (cols - 1) * gap;
  const gridD = rows * sz + (rows - 1) * gap;
  const W = r3(Math.max(24, gridW + 2));
  const D = r3(Math.max(18, gridD + 3.6));
  const back = -D / 2 + 0.6;
  const env: EnvPlacement[] = [
    { prefab: "platform", id: "floor", at: [0, 0, 0], size: [W, 0.2, D] },
  ];
  const sections: RelaySection[] = [];
  stages.forEach((plan, i) => {
    const row = Math.floor(i / cols);
    const k = i % cols;
    const col = row % 2 === 0 ? k : cols - 1 - k;
    const yaw: Yaw = row % 2 === 0 ? 0 : 180;
    const ox = r3(-gridW / 2 + sx / 2 + col * px);
    const oz = r3(back + sz / 2 + row * pz);
    const [c, s] = ROT[yaw];
    /** 지역 → 월드(밑면 · m) */
    const w = (p: Vec3): Vec3 => [r3(ox + p[0] * c + p[2] * s), p[1], r3(oz - p[0] * s + p[2] * c)];
    const slot = (sl: Slot): Slot => ({ at: w(sl.at), yaw: addYaw(sl.yaw ?? 0, yaw) });
    const n = i + 1;
    const t = RELAY_WALL_T;
    const walls: [string, Vec3, Vec3][] = [
      ["in", [-sx / 2 + t / 2, 0, 0], [t, wallH, sz]],
      ["out", [sx / 2 - t / 2, 0, 0], [t, wallH, sz]],
      ["l", [0, 0, -sz / 2 + t / 2], [sx - 2 * t, wallH, t]],
      ["r", [0, 0, sz / 2 - t / 2], [sx - 2 * t, wallH, t]],
    ];
    for (const [name, at, size] of walls)
      env.push({
        prefab: "wall",
        id: `s${n}-wall-${name}`,
        at: w(at),
        yaw,
        size: [r3(size[0]), size[1], r3(size[2])],
      });
    const ex = sec.exit;
    const exitAt = w(ex.at);
    env.push({ prefab: "goal-zone", id: `s${n}-exit`, at: exitAt, yaw, size: ex.size });
    // 구간 yaw 는 0 · 180 뿐이라 축 정렬 반 크기는 지역과 같다
    const exitBox = {
      center: [exitAt[0], r3(exitAt[1] + ex.size[1] / 2), exitAt[2]] as Vec3,
      half: [r3(ex.size[0] / 2), r3(ex.size[1] / 2), r3(ex.size[2] / 2)] as Vec3,
    };
    let lever: string | undefined;
    if (plan.out === "lever") {
      lever = `s${n}-lever`;
      env.push({ prefab: "lever", id: lever, at: exitAt, yaw, size: ex.size });
    }
    const entry = slot(sec.entry);
    const objectKind: "ball" | "crate" | undefined =
      plan.in === "ball" || plan.out === "ball"
        ? "ball"
        : plan.out === "zone"
          ? "crate"
          : undefined;
    let objectId: string | undefined;
    let objectSlot: Slot | undefined;
    if (objectKind) {
      const o = RELAY_OBJECTS[objectKind];
      objectId = `s${n}-${objectKind}`;
      objectSlot = plan.in === "ball" ? entry : slot(sec.object);
      env.push({
        prefab: o.prefab,
        id: objectId,
        at: objectSlot.at,
        yaw: objectSlot.yaw ?? 0,
        size: o.size,
        mass: o.mass,
        ...("friction" in o ? { friction: o.friction } : {}),
      });
    }
    sections.push({
      index: i,
      machine: plan.machine,
      origin: [ox, oz],
      yaw,
      machineSlot: slot(sec.machine),
      entry,
      exitBox,
      exitZone: `s${n}-exit`,
      ...(lever ? { lever } : {}),
      ...(objectId ? { objectId, objectSlot: objectSlot!, objectKind: objectKind! } : {}),
      inKind: plan.in,
      outKind: plan.out,
      timeoutS: plan.timeoutS ?? r.stageTimeoutS,
    });
  });
  // 구경 자리 — 앞 가장자리 두 줄(15 × 2 · 1 m 간격) · 트랙(−z)을 본다
  const bench: Slot[] = [];
  for (let i = 0; i < 30; i++) {
    const row = Math.floor(i / 15);
    bench.push({ at: [r3(-7 + (i % 15)), 0, r3(D / 2 - 0.7 - row * 1.2)], yaw: 180 });
  }
  return {
    def: derive(def, key, env),
    presetKey: key,
    kind: "relay",
    arenas: [],
    lanes: [],
    bench,
    sections,
    track: { columns: cols, rows, pitch: [r3(px), 0, r3(pz)] },
  };
}
